import {
    FacebookShareButton,
    LinkedinShareButton,
    PinterestShareButton,
    WhatsappShareButton,
} from 'react-share';
import {
    FacebookCircleFill,
    LinkedinBoxFill,
    WhatsappFill,
    PinterestFill,
    EditorLink,
} from '@archipro-design/icons';
import type { SocialMediaShareType } from '~/modules/design-board/config';
import { useRootData } from '~/modules/root';
import { useStyles, Flex } from '@archipro-design/aria';
import * as S from './SocialMediaShareButtons.style';
import { useTracker } from '@archipro-website/tracker';
import { useToaster } from '~/modules/root/context/ToasterContext';

interface SocialMediaShareButtonsProps {
    link: string;
    imageUrl?: string;
    title?: string;
    description?: string;
    isBoard?: boolean;
    onClick?: (socialType: SocialMediaShareType) => void;
    onClose?: () => void;
    closeParentModal?: () => void;
}

export const SocialMediaShareButtons = ({
    isBoard = false,
    onClick,
    onClose,
    link,
    imageUrl,
    title,
    description = 'Archipro',
    closeParentModal,
}: SocialMediaShareButtonsProps) => {
    const { env } = useRootData();
    const { css } = useStyles();
    const tracker = useTracker();
    const { toast } = useToaster();

    const urlPrefix =
        env.stage === 'Production' && typeof window !== 'undefined'
            ? window.location.origin
            : 'https://archipro.co.nz';

    const shareUrl = link?.startsWith('http') ? link : `${urlPrefix}${link}`;
    const shareTitle = isBoard ? 'Archipro Design Board:' + title : title;

    const onCopyLink = () => {
        navigator.clipboard.writeText(shareUrl);
        onShareClick('copyLink');
        toast('Link copied to clipboard', { type: 'success' });
        closeParentModal && closeParentModal();
    };

    const onShareClick = (socialType: SocialMediaShareType) => {
        onClick && onClick(socialType);
        tracker.log('Share', {
            url: new URL(shareUrl),
            targetTracker: 'archiproTracker',
            data: {
                ExtraData: socialType,
            },
        });
    };

    return (
        <div className={css(S.ButtonsWrapper)}>
            <div className={css(S.SocialMediaIcons)}>
                <FacebookShareButton
                    url={shareUrl}
                    title={shareTitle}
                    onShareWindowClose={onClose}
                    type={'button'}
                >
                    <FacebookCircleFill
                        onClick={() => onShareClick('facebook')}
                    />
                </FacebookShareButton>

                <LinkedinShareButton
                    url={shareUrl}
                    title={shareTitle}
                    onShareWindowClose={onClose}
                    type={'button'}
                >
                    <LinkedinBoxFill onClick={() => onShareClick('linkedin')} />
                </LinkedinShareButton>

                {imageUrl && (
                    <PinterestShareButton
                        url={shareUrl}
                        title={shareTitle}
                        media={imageUrl}
                        description={description}
                        onShareWindowClose={onClose}
                        type={'button'}
                    >
                        <PinterestFill
                            onClick={() => onShareClick('pinterest')}
                        />
                    </PinterestShareButton>
                )}

                <WhatsappShareButton
                    url={shareUrl}
                    title={shareTitle}
                    onShareWindowClose={onClose}
                    type={'button'}
                >
                    <WhatsappFill onClick={() => onShareClick('whatsapp')} />
                </WhatsappShareButton>
            </div>
            <Flex className={css(S.CopyLink)} onClick={onCopyLink}>
                <div>
                    <EditorLink />
                </div>
                <div>Copy {isBoard ? 'design board' : 'pin'} link</div>
            </Flex>
        </div>
    );
};
